import { useState } from "react";
import { EnvelopeSimple, Clock, MapPin, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { Eyebrow, BookDemoButton } from "@/components/marketing/Primitives";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const empty = { name: "", company: "", email: "", telephone: "", business_size: "", message: "" };

const details = [
  { icon: EnvelopeSimple, label: "Email", value: "support@flowcoreplus.co.uk" },
  { icon: Clock, label: "Office hours", value: "Mon–Fri, 9:00am – 5:00pm" },
];

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", { ...form, enquiry_type: "contact" });
      setDone(true);
      setForm(empty);
      toast.success("Message sent — we'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact FlowCore — Book a Demo or Get in Touch"
        description="Get in touch with the FlowCore team or book a live demo. Email, phone and office hours available. We're ready to help your field service business run leaner."
      />
      <section className="relative overflow-hidden pt-36 pb-12">
        <div className="pointer-events-none absolute inset-0 radial-fade" />
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Reveal>
            <Eyebrow className="mx-auto">Contact</Eyebrow>
            <h1 className="mt-6 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Let's talk field service
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-muted-foreground md:text-lg">
              Whether you're ready for a demo or just have a question, we'd love to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-5 sm:px-8 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <div className="rounded-3xl border border-white/10 bg-card p-8">
              <h2 className="font-heading text-2xl font-bold text-white">Send us a message</h2>
              <p className="mt-1 text-sm text-muted-foreground">Fill in the form and our team will respond within one working day.</p>

              {done ? (
                <div className="py-12 text-center" data-testid="contact-success">
                  <CheckCircle size={60} weight="fill" className="mx-auto text-primary" />
                  <h3 className="mt-4 text-xl font-semibold text-white">Message received</h3>
                  <p className="mt-2 text-muted-foreground">Thanks for reaching out — we'll be in touch very soon.</p>
                  <button onClick={() => setDone(false)} className="mt-6 rounded-full bg-white/10 px-6 py-2.5 font-semibold text-white hover:bg-white/15" data-testid="contact-reset">
                    Send another
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="contact-form">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="c-name" className="text-xs text-muted-foreground">Name *</Label>
                      <Input id="c-name" data-testid="contact-name" value={form.name} onChange={update("name")} placeholder="Jane Smith" className="mt-1 bg-background" />
                    </div>
                    <div>
                      <Label htmlFor="c-company" className="text-xs text-muted-foreground">Company</Label>
                      <Input id="c-company" data-testid="contact-company" value={form.company} onChange={update("company")} placeholder="Acme Ltd" className="mt-1 bg-background" />
                    </div>
                    <div>
                      <Label htmlFor="c-email" className="text-xs text-muted-foreground">Email *</Label>
                      <Input id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={update("email")} placeholder="jane@acme.co.uk" className="mt-1 bg-background" />
                    </div>
                    <div>
                      <Label htmlFor="c-phone" className="text-xs text-muted-foreground">Telephone</Label>
                      <Input id="c-phone" data-testid="contact-phone" value={form.telephone} onChange={update("telephone")} placeholder="07000 000000" className="mt-1 bg-background" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="c-msg" className="text-xs text-muted-foreground">Message</Label>
                    <Textarea id="c-msg" data-testid="contact-message" value={form.message} onChange={update("message")} placeholder="How can we help?" className="mt-1 min-h-[120px] bg-background" />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60"
                    data-testid="contact-submit"
                  >
                    {submitting ? "Sending…" : (<><PaperPlaneTilt size={18} weight="bold" /> Send message</>)}
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          <div className="space-y-6 lg:col-span-2">
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-primary/40 bg-card p-8 glow-green">
                <h3 className="font-heading text-xl font-bold text-white">Prefer a live walkthrough?</h3>
                <p className="mt-2 text-sm text-muted-foreground">Book a personalised demo and see FlowCore in action with your own workflows in mind.</p>
                <BookDemoButton testid="contact-book-demo" className="mt-5 w-full justify-center" />
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="space-y-4 rounded-3xl border border-white/10 bg-card p-8">
                {details.map((d) => {
                  const Icon = d.icon;
                  return (
                    <div key={d.label} className="flex items-start gap-3">
                      <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <Icon size={20} weight="duotone" />
                      </span>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-muted-foreground">{d.label}</p>
                        <p className="text-sm font-medium text-white">{d.value}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="flex h-40 items-center justify-center rounded-3xl border border-white/10 bg-card text-center dotgrid">
                <div>
                  <MapPin size={28} weight="duotone" className="mx-auto text-primary" />
                  <p className="mt-2 text-sm text-muted-foreground">Map placeholder — add your office location</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
